// v0.5.0d — Manual-override config (frontend mirror of
// `/app/backend/country_returns/manual_override_config.py`).
//
// MUST stay in lockstep with the backend file. The pytest gate
// `test_v050d_manual_override_config_parity` cross-checks the
// two so a divergence fails the build.
//
// Sign-off 2026-06-20 + 2026-06-20d correction on FR/ES box IDs:
//   - DE Kz 35 (UStVA) — typed_in output, "Lieferungen zu anderen
//     Steuersätzen".
//   - FR A2 (CA3) — typed_in output, "Autres opérations
//     imposables". The printed-form label for the same line is
//     "Ligne 9F"; the code uses "A2" as the internal box_id.
//   - ES — none. Modelo 303 has no output-side "other rates"
//     box (Casilla 28 is input-deductible). ES joins UK/IT in
//     the "Supersede or wait IS the answer" bucket.
//   - UK — none. HMRC Notice 700 §19 record-keeping requires
//     resolving the rate at source.
//   - IT — none. LIPE requires every euro in a VP-line.

// v0.6.6 fix — was keyed "UK". uk_vat_return/boxes.py (backend) declares
// country="GB" (v0.4.64 — ISO 3166-1 alpha-2, was "UK") and that's the
// real runtime country code the API returns and this file's own callers
// pass in. The frontend/backend parity test only compares the two files
// against EACH OTHER, so both being stale in lockstep never tripped it —
// noOverrideReasonFor("GB") silently returned null for every UK offender,
// so OffenderInspectDrawer never rendered the HMRC explanation this
// module exists to surface.
export const MANUAL_OVERRIDE_BOX = Object.freeze({
  DE: "35",
  FR: "A2",
  ES: null,
  GB: null,
  IT: null,
  // A2-234 (G6) — see the backend file for the sources.
  AT: null, CZ: null, DK: null, HU: null, NL: null, PT: null, SE: null,
});

export const NO_OVERRIDE_REASON = Object.freeze({
  ES:
    "Modelo 303 has no output-side 'other rates' box. Casilla 28 "
    + "on the real form is input-deductible (Op. interiores "
    + "corrientes), not an output catch-all. Supersede the "
    + "offending txns OR wait for the maintainer to add the rate "
    + "to the table.",
  GB:
    "The 9-Box VAT Return form IS rate-agnostic at submission — "
    + "but HMRC Notice 700 §19 record-keeping rules require "
    + "resolving the rate at source before submission. An "
    + "'unknown rate' in the records is itself a record-keeping "
    + "failure. Supersede the offending txns (fix at source) OR "
    + "wait for the maintainer to add the rate.",
  IT:
    "LIPE requires every euro to land in a rate-specific VP-line. "
    + "There is no 'other rates' bucket. Supersede the offending "
    + "txns OR wait for the maintainer to add the rate.",
  AT:
    "The U30 has a line for each statutory rate (20 %, 13 %, 10 %, "
    + "4,9 %, 19 %) and none for another rate. Supersede the offending "
    + "txns OR wait for the maintainer to add the rate.",
  CZ:
    "The DPH return has only base-rate and reduced-rate lines (ř. "
    + "1-2); there is no line for another rate. Supersede the offending "
    + "txns OR wait for the maintainer to add the rate.",
  DK:
    "The momsangivelse reports one total of salgsmoms and Denmark has "
    + "a single 25 % rate, so a sale at another rate is a mistake to "
    + "fix at source. Supersede the offending txns OR wait for the "
    + "maintainer to add the rate.",
  HU:
    "The 65A has a row for each rate (5 %, 18 %, 27 %); row 35 "
    + "'Egyéb' is the reverse-charge row, not an 'other rates' line. "
    + "Supersede the offending txns OR wait for the maintainer to add "
    + "the rate.",
  NL:
    "Rubriek 1c is only for a sports canteen paying the 13 % forfait, "
    + "and the Belastingdienst's 2026 instructions say it does not "
    + "apply to a business established abroad. Supersede the offending "
    + "txns OR wait for the maintainer to add the rate.",
  PT:
    "The declaração periódica has fields per rate (reduzida, "
    + "intermédia, normal) for each region and none for another rate. "
    + "Supersede the offending txns OR wait for the maintainer to add "
    + "the rate.",
  SE:
    "The momsdeklaration has output-VAT boxes for 25 %, 12 % and 6 % "
    + "only (rutor 10-12). Supersede the offending txns OR wait for the "
    + "maintainer to add the rate.",
});

export const countrySupportsManualOverride = (country) => {
  const cc = (country || "").toUpperCase();
  return MANUAL_OVERRIDE_BOX[cc] != null;
};

export const manualOverrideBoxFor = (country) =>
  MANUAL_OVERRIDE_BOX[(country || "").toUpperCase()] || null;

export const noOverrideReasonFor = (country) =>
  NO_OVERRIDE_REASON[(country || "").toUpperCase()] || null;
